import React from 'react';
import queryString from 'query-string';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';
import { useReactTable, getCoreRowModel } from '@tanstack/react-table';

import { useBackofficeTable } from 'common/hooks';
import { baseTableOptions } from 'common/constants/tables';
import { getInitialSorting } from 'common/utils';

import NewBaseTable from '../NewBaseTable';
import { TablePagination } from '../NewBaseTable/_components';
import { staticStyles } from './style';

const BOPaginationTable = ({ data, columns, params, getList, tableKey, loading, emptyMessage }) => {
  const location = useLocation();
  const history = useHistory();

  const {
    columnsList,
    savedColSizes,
    columnOrder,
    defaultColOrder,
    setColumnOrder,
    columnVisibility,
    setColumnVisibility,
  } = useBackofficeTable({ tableKey, columns });

  const sorting = getInitialSorting({ searchParamsString: location.search, columnsList });

  const onSortingChange = updater => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;

    const { page: currentPage, page_size: pageSize } = queryString.parse(location.search);

    const resetSorting = () => {
      const newSearch = {
        ...params.search,
        page: currentPage || '1',
        page_size: pageSize || '10',
        ordering: null,
      };

      history.replace({
        ...location,
        search: queryString.stringify(newSearch, { skipNull: true }),
      });

      getList({ search: newSearch });
    };

    if (!next.length) {
      resetSorting();
      return;
    }

    const { id: columnId, desc } = next[0];
    const column = columnsList.find(c => c.id === columnId);

    if (!column) {
      return;
    }

    const accessorKey = column.accessorKey;
    const ordering = desc ? '' : '-';

    const newParams = {
      page: currentPage || '1',
      page_size: pageSize || '10',
      ordering: `${ordering}${accessorKey}`,
    };

    history.replace({
      ...location,
      search: queryString.stringify({
        ...params.search,
        ...newParams,
      }),
    });

    getList({
      search: {
        ...params.search,
        ...newParams,
      },
    });
  };

  const table = useReactTable({
    data: data.results,
    columns: columnsList,
    manualPagination: true,
    manualSorting: true,
    getCoreRowModel: getCoreRowModel(),
    initialState: {
      columnSizing: savedColSizes,
      sorting,
      columnOrder: defaultColOrder,
    },
    state: {
      columnVisibility,
      columnOrder,
      sorting,
    },
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onSortingChange,
    ...baseTableOptions,
  });

  const handleTableSortingRefresh = () => {
    if (queryString.parse(location.search)?.ordering) {
      history.replace({
        ...location,
        search: queryString.stringify(
          {
            ...params.search,
            ordering: null,
          },
          { skipNull: true }
        ),
      });
    }
  };

  return (
    <div>
      <NewBaseTable
        emptyMessage={emptyMessage}
        table={table}
        tableKey={tableKey}
        loading={loading}
        handleRefresh={handleTableSortingRefresh}
      />
      {!loading && data.count > 0 && <TablePagination dataCount={data.count} params={params} getList={getList} />}

      <style jsx global>
        {staticStyles}
      </style>
    </div>
  );
};

BOPaginationTable.propTypes = {
  data: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object), PropTypes.object]).isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      headerKey: PropTypes.string,
      accessorKey: PropTypes.string,
      cell: PropTypes.func,
    })
  ).isRequired,
  params: PropTypes.object.isRequired,
  getList: PropTypes.func,
  tableKey: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  emptyMessage: PropTypes.node,
};

BOPaginationTable.defaultProps = {
  emptyMessage: null,
  // URL-driven pages fetch their data when location.search changes.
  getList: () => {},
};

export default BOPaginationTable;
